import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { BASE_URL, GET_RECIEPT_REPORT, CANCEL_RECIEPT, POST_APPROVAL_ENTRY } from '../../utlilities/config';
import { initializeWebUserInfo, getWebUserInfo } from '../../utlilities/webConfig';
import { ReportEmbedContext } from '../reportEmbedContext';
import withAuth from '../../common/auth';
import HighlightMetricsModal from './components/HighlightMetricsModal';
import ReceiptNormalComponent from '../../components/NormalReciept';
import ReceiptKotComponent from '../../components/KotReciept';
import { toPng } from 'html-to-image';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

// Icons
import {
  FiDownload, FiBarChart2, FiFileText, FiTrash2, FiSearch, FiFilter,
  FiArrowUp, FiArrowDown, FiX, FiPrinter,
} from 'react-icons/fi';
import { MdCurrencyRupee } from 'react-icons/md';

// Animated loader component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading report data...</p>
    </div>
  </div>
);

const RestrictedBlur = ({ restricted, children, className = '' }) => (
  <div className={`relative ${className}`}>
    <div className={restricted ? 'pointer-events-none select-none blur-sm opacity-60' : ''}>
      {children}
    </div>
    {restricted && (
      <div
        className="absolute inset-0 z-10 cursor-not-allowed rounded-lg bg-slate-100/20"
        title="You don't have permission to view pharmacy report amounts"
      />
    )}
  </div>
);

// Dashboard Card Component
const StatCard = ({ title, value, subtitle }) => {
  return (
    <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
        {title}
      </p>
      <p className="mt-1 text-md font-semibold text-indigo-600">
        {value.toString().includes('₹') ? value : value}
      </p>
      {subtitle && (
        <p className="text-xs text-indigo-500 mt-1">{subtitle}</p>
      )}
    </div>
  );
};

// Pharmacy Collection Table (aligned with OP CollectionTable)
const PharmacyCollectionTable = ({
  bills,
  token,
  setLoading,
  getCollectionData,
  canShowPhReports = true,
  approvalNeededPaymentReceiptCancel = false,
  sysInfo = {},
  clientInfo = {},
  userInfo = {},
  generalsettings = {},
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [hoverRow, setHoverRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPaymentModes, setSelectedPaymentModes] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [permissions, setPermissions] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const filterDropdownRef = useRef(null);

  const isElectron = () => window && window.process && window.process.type;

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  const paymentModeFilters = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Bank', label: 'Bank' },
  ];

  useEffect(() => {
    const fetchPermissions = async () => {
      let userRolesData;
      if (isElectron()) {
        userRolesData = await ipcRenderer.invoke('store-get', 'user_role');
      } else {
        initializeWebUserInfo();
        userRolesData = getWebUserInfo().user_role;
      }
      setPermissions(processPermissions(userRolesData || []));
    };
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };
    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);

  const processPermissions = (roles) => {
    const permissionsMap = {};
    if (!Array.isArray(roles)) return permissionsMap;
    roles.forEach((role) => {
      (role?.modules || []).forEach((module) => {
        permissionsMap[module.moduleName] = module.permissions;
      });
    });
    return permissionsMap;
  };

  const hasPermission = (moduleName, action) => permissions[moduleName]?.[action] || false;

  const canCancelPayment =
    userInfo?.Designation === 'Admin' || hasPermission('Cancell Pharmacy Payment', 'Create');

  const formatAmount = (amount) => parseFloat(amount || 0).toFixed(2);

  const formatInvoiceDateLabel = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '';
    if (typeof raw === 'string') {
      const ymd = raw.trim().slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
        const d = parseISO(`${ymd}T12:00:00`);
        if (isValid(d)) return format(d, 'dd-MM-yyyy');
      }
    }
    const d = typeof raw === 'string' ? parseISO(raw) : new Date(raw);
    if (!isValid(d)) return String(raw);
    return format(d, 'dd-MM-yyyy');
  };

  const formatReceiptDateTime = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '—';
    const d = typeof raw === 'string' ? parseISO(raw) : new Date(raw);
    if (!isValid(d)) return String(raw);
    return format(d, 'dd-MM-yyyy hh:mm a');
  };

  const invoiceDatesSummary = (receipt) => {
    const lines = receipt.invoiceDetails;
    if (!Array.isArray(lines) || lines.length === 0) return '—';
    const keys = lines
      .map((inv) => inv?.InvDate ?? inv?.invDate)
      .filter(Boolean)
      .map((raw) => {
        const label = formatInvoiceDateLabel(raw);
        const sortKey = typeof raw === 'string' ? raw.slice(0, 10) : String(raw);
        return { sortKey, label };
      });
    if (keys.length === 0) return '—';
    const seen = new Set();
    const unique = [];
    keys.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    for (const k of keys) {
      if (seen.has(k.label)) continue;
      seen.add(k.label);
      unique.push(k.label);
    }
    return unique.join(', ');
  };

  const cancelPaymentAPI = async (receiptNo, sequence, reason) => {
    return axios.post(`${BASE_URL}${CANCEL_RECIEPT}`, {}, {
      headers: {
        XApiKey: process.env.REACT_APP_API_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ReceiptNo: receiptNo,
        Sequence: sequence,
        cancelReason: reason,
      },
    });
  };

  const submitCancelApprovalRequest = async (receiptNo, sequence, reason) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}${POST_APPROVAL_ENTRY}`,
        {
          entryName: 'PaymentReceipt',
          eventType: 'Cancel',
          sequence: sequence ?? null,
          invoiceNo: receiptNo ?? null,
          editNo: null,
          addedUserName: userInfo?.UserName ?? null,
          reason: reason ?? null,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            XApiKey: process.env.REACT_APP_API_KEY,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        await cancelPaymentAPI(receiptNo, sequence, reason);
        await Swal.fire({
          title: 'Success!',
          text: 'Payment has been cancelled successfully.',
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
        });
        getCollectionData();
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Could not cancel payment. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPayment = async (receiptNo, sequence, patientName) => {
    if (!canCancelPayment) {
      Swal.fire({
        title: 'Access Denied',
        text: "You don't have permission to cancel payments.",
        icon: 'error',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    if (approvalNeededPaymentReceiptCancel) {
      const trimmedName = patientName && String(patientName).trim();
      const confirmResult = await Swal.fire({
        title: 'Send approval to admin?',
        text: trimmedName
          ? `Send an approval request to cancel the payment receipt for ${trimmedName}?`
          : 'Send an approval request to the admin to cancel this payment receipt?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4f46e5',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, send request',
        cancelButtonText: 'Cancel',
      });
      if (!confirmResult.isConfirmed) return;

      const reasonResult = await Swal.fire({
        title: 'Cancellation Reason',
        html: `
          <div class="text-left">
            <label for="swal-approval-reason" class="block text-sm font-medium text-gray-700 mb-2">
              Please provide a reason for this cancel approval request:
            </label>
            <textarea
              id="swal-approval-reason"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              rows="4"
              placeholder="Enter reason for cancellation..."
            ></textarea>
          </div>
        `,
        showCancelButton: true,
        confirmButtonColor: '#4f46e5',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Submit Request',
        cancelButtonText: 'Cancel',
        preConfirm: () => {
          const reason = document.getElementById('swal-approval-reason')?.value?.trim();
          if (!reason) {
            Swal.showValidationMessage('Please provide a reason for cancellation');
            return false;
          }
          return reason;
        },
        allowOutsideClick: () => !Swal.isLoading(),
      });

      if (reasonResult.isConfirmed) {
        await submitCancelApprovalRequest(receiptNo, sequence, reasonResult.value);
      }
      return;
    }

    const confirmResult = await Swal.fire({
      title: 'Cancel Payment',
      text: 'Are you sure you want to cancel this payment?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, cancel it!',
      cancelButtonText: 'No, keep it',
    });

    if (!confirmResult.isConfirmed) return;

    const reasonResult = await Swal.fire({
      title: 'Cancellation Reason',
      html: `
        <div class="text-left">
          <label for="swal-reason" class="block text-sm font-medium text-gray-700 mb-2">
            Please provide a reason for cancellation:
          </label>
          <textarea
            id="swal-reason"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
            rows="4"
            placeholder="Enter reason for cancellation..."
          ></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Submit Cancellation',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const reason = document.getElementById('swal-reason').value.trim();
        if (!reason) {
          Swal.showValidationMessage('Please provide a reason for cancellation');
          return false;
        }
        return reason;
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (!reasonResult.isConfirmed) return;

    try {
      setLoading(true);
      await cancelPaymentAPI(receiptNo, sequence, reasonResult.value);
      await Swal.fire({
        title: 'Success!',
        text: 'Payment has been cancelled successfully.',
        icon: 'success',
        confirmButtonColor: '#10b981',
        confirmButtonText: 'OK',
      });
      getCollectionData();
    } catch (error) {
      await Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to cancel payment. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
      });
    } finally {
      setLoading(false);
    }
  };

  const flattenedReceipts = useMemo(() => {
    if (!bills || bills.length === 0) return [];
    const receipts = [];
    bills.forEach((day) => {
      if (day.receipts?.length > 0) {
        day.receipts.forEach((receipt) => {
          receipts.push({
            ...receipt,
            receiptDate:
              receipt.createdDate ||
              receipt.CreatedDate ||
              receipt.receiptDate ||
              receipt.ReceiptDate ||
              day.date,
          });
        });
      }
    });
    return receipts;
  }, [bills]);

  const filteredAndSortedReceipts = useMemo(() => {
    let filtered = [...flattenedReceipts];

    if (selectedPaymentModes.length > 0) {
      filtered = filtered.filter((receipt) => {
        const hasCash = receipt.cashAmount > 0;
        const hasBank = receipt.bankAmount > 0;
        if (selectedPaymentModes.includes('Cash') && selectedPaymentModes.includes('Bank')) {
          return hasCash || hasBank;
        }
        if (selectedPaymentModes.includes('Cash')) return hasCash;
        if (selectedPaymentModes.includes('Bank')) return hasBank;
        return false;
      });
    }

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((receipt) => {
        const receiptNoStr = receipt.receiptNo?.toString().toLowerCase() || '';
        const patientNameStr = receipt.patientName?.toString().toLowerCase() || '';
        const receiptDateStr = receipt.receiptDate?.toString().toLowerCase() || '';
        const hasMatchingInvoice = receipt.invoiceDetails?.some((inv) =>
          inv.InvoiceNo?.toString().toLowerCase().includes(lowerSearchTerm)
        ) || false;
        const invDatesStr = invoiceDatesSummary(receipt).toLowerCase();
        return receiptNoStr.includes(lowerSearchTerm) ||
          patientNameStr.includes(lowerSearchTerm) ||
          receiptDateStr.includes(lowerSearchTerm) ||
          invDatesStr.includes(lowerSearchTerm) ||
          hasMatchingInvoice;
      });
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (sortConfig.key === 'totalAmount') {
          aVal = (a.cashAmount || 0) + (a.bankAmount || 0);
          bVal = (b.cashAmount || 0) + (b.bankAmount || 0);
        }
        if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
          return sortConfig.direction === 'ascending'
            ? Number(aVal) - Number(bVal)
            : Number(bVal) - Number(aVal);
        }
        if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [flattenedReceipts, selectedPaymentModes, searchTerm, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const togglePaymentModeFilter = (paymentMode) => {
    setSelectedPaymentModes((prev) =>
      prev.includes(paymentMode) ? prev.filter((m) => m !== paymentMode) : [...prev, paymentMode]
    );
  };

  const clearFilters = () => {
    setSelectedPaymentModes([]);
    setSearchTerm('');
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return null;
    return sortConfig.direction === 'ascending'
      ? <FiArrowUp size={14} className="ml-1" />
      : <FiArrowDown size={14} className="ml-1" />;
  };

  const handlePrintReceipt = async (receipt) => {
    if (!receipt) {
      toast.error('Receipt data not found', { duration: 1000 });
      return;
    }

    let parsedLineJsonData = [];
    try {
      if (typeof receipt.LineJsonData === 'string' && receipt.LineJsonData.trim()) {
        parsedLineJsonData = JSON.parse(receipt.LineJsonData);
      }
    } catch (error) {
      console.warn('Unable to parse LineJsonData for receipt print:', error);
    }

    const invoiceDetails = Array.isArray(receipt.invoiceDetails) && receipt.invoiceDetails.length > 0
      ? receipt.invoiceDetails
      : parsedLineJsonData;

    const totalPayingAmount =
      Number(receipt.cashAmount ?? receipt.CashAmount ?? 0) +
      Number(receipt.bankAmount ?? receipt.BankAmount ?? 0);

    const departmentSummary = new Map();
    (invoiceDetails || []).forEach((invoice) => {
      const departmentName = 'Pharmacy';
      const invoiceNo = invoice?.InvoiceNo ?? invoice?.invoiceNo ?? 0;
      const payingAmount = Number(invoice?.PayingAmout ?? invoice?.payingAmount ?? 0);
      const totalAmount = Number(invoice?.TotalAmount ?? invoice?.totalAmount ?? payingAmount);
      const remainingAmount = Math.max(totalAmount - payingAmount, 0);

      if (!departmentSummary.has(departmentName)) {
        departmentSummary.set(departmentName, {
          departmentName,
          billCount: 0,
          totalPaying: 0,
          totalRemaining: 0,
          bills: [],
        });
      }

      const dept = departmentSummary.get(departmentName);
      dept.billCount += 1;
      dept.totalPaying += payingAmount;
      dept.totalRemaining += remainingAmount;
      dept.bills.push({ invoiceNo, payingAmount });
    });

    const departmentTotals = Array.from(departmentSummary.values());
    const printReceiptData = {
      receiptNumber: receipt.receiptNo ?? receipt.ReceiptNo ?? 0,
      paymentMethod: receipt.paymentMode ?? receipt.PaymentMode ?? 'Cash',
      isMultiplePatients: false,
      patients: [{
        patientName: receipt.patientName ?? receipt.PatientName ?? '',
        patientId: receipt.patientId ?? receipt.PatientID ?? 0,
      }],
      departmentTotals,
      billCount: departmentTotals.reduce((sum, dept) => sum + Number(dept.billCount || 0), 0),
      totalDueAmount: departmentTotals.reduce(
        (sum, dept) => sum + Number(dept.totalPaying || 0) + Number(dept.totalRemaining || 0),
        0
      ),
      totalPayingAmount,
      totalRemainingAmount: departmentTotals.reduce((sum, dept) => sum + Number(dept.totalRemaining || 0), 0),
      ...receipt,
      invoiceDetails,
      LineJsonData: receipt.LineJsonData ?? JSON.stringify(invoiceDetails || []),
    };

    setSelectedDate(printReceiptData.receiptDate || printReceiptData.ReceiptDate || '');
    setReceiptData(printReceiptData);
    setTimeout(() => handlePrint(), 120);
  };

  const printKOT = async () => {
    if (!isElectron()) {
      toast.error('Not Allowed', { duration: 1000 });
      return;
    }
    if (!sysInfo.opRecieptPrinter) {
      toast.error('Cannot Print, Printer Not Selected', { duration: 1000 });
      return;
    }

    const receiptDiv = document.getElementById('receiptDiv');
    if (!receiptDiv) {
      toast.error('KOT element not found', { duration: 1000 });
      return;
    }

    try {
      const kotHTML = receiptDiv.cloneNode(true).outerHTML;
      const printCSS = `@page { size: 80mm auto; margin: 0; }`;
      ipcRenderer.once('kot-print-success', (event, { status, error }) => {
        if (status === 'success') {
          setLoading(false);
          setReceiptData(null);
          toast.success('KOT printed successfully!', { duration: 1000 });
        } else {
          toast.error('KOT Print failed. Please check your printer settings.', { autoClose: 1000 });
          setLoading(false);
        }
      });
      ipcRenderer.send('print-kot-html', {
        htmlContent: kotHTML,
        cssContent: printCSS,
        printer: sysInfo.opRecieptPrinter,
      });
    } catch (error) {
      toast.error('Failed to prepare KOT for printing.', { autoClose: 1000 });
    }
  };

  const handlePrint = async () => {
    if (generalsettings?.RecieptType === 'KOT') {
      printKOT();
      return;
    }
    if (!isElectron()) {
      toast.error('Not Allowed', { duration: 1000 });
      return;
    }
    if (!sysInfo.opRecieptPrinter) {
      toast.error('Cannot Print, Printer Not Selected', { duration: 1000 });
      return;
    }

    const receiptDiv = document.getElementById('receiptDiv');
    if (!receiptDiv) return;

    try {
      const promises = [];
      for (let i = 1; i <= totalPages; i++) {
        await new Promise((resolve) => {
          setCurrentPage(i);
          setTimeout(resolve, 600);
        });
        const dataUrl = await toPng(receiptDiv, { quality: 1, pixelRatio: 3 });
        promises.push(dataUrl);
      }

      ipcRenderer.once('print-success', (event, { status, error }) => {
        if (status !== 'success') {
          toast.error('Print failed. Please check your printer settings.', { autoClose: 1000 });
        }
      });

      const images = await Promise.all(promises);
      images.forEach((imgDataUrl) => {
        ipcRenderer.send('print-bill-image', {
          imgDataUrl,
          printer: sysInfo.opRecieptPrinter,
        });
      });
      setTimeout(() => {
        setLoading(false);
        setReceiptData(null);
      }, 1000);
    } catch (error) {
      toast.error('Failed to prepare pages for printing.', { autoClose: 1000 });
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 flex flex-col h-full overflow-hidden">
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
          <div className="text-sm text-gray-700 font-medium">
            <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs font-semibold">
              {filteredAndSortedReceipts.length}{' '}
              {filteredAndSortedReceipts.length === 1 ? 'receipt' : 'receipts'} found
            </span>
            {(selectedPaymentModes.length > 0 || searchTerm) && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-2 text-xs text-indigo-600 hover:text-indigo-800 underline"
              >
                Clear filters
              </button>
            )}
          </div>

          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                <FiSearch size={14} />
              </div>
              <input
                type="text"
                placeholder="Search receipt, invoice, patient..."
                className="pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="relative" ref={filterDropdownRef}>
              <button
                type="button"
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className={`p-2 rounded-lg transition-all duration-200 shadow-sm border ${
                  selectedPaymentModes.length > 0
                    ? 'bg-indigo-100 text-indigo-600 border-indigo-300'
                    : 'text-gray-500 hover:bg-white hover:text-indigo-600 border-gray-200'
                }`}
              >
                <FiFilter size={18} />
                {selectedPaymentModes.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {selectedPaymentModes.length}
                  </span>
                )}
              </button>

              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-30">
                  <div className="p-2">
                    <div className="text-xs font-semibold text-gray-700 mb-2 px-2">Filter by Payment Mode</div>
                    {paymentModeFilters.map((paymentMode) => (
                      <label
                        key={paymentMode.value}
                        className="flex items-center px-2 py-1.5 hover:bg-gray-50 cursor-pointer rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPaymentModes.includes(paymentMode.value)}
                          onChange={() => togglePaymentModeFilter(paymentMode.value)}
                          className="mr-2 rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">{paymentMode.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedPaymentModes.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedPaymentModes.map((paymentMode) => {
              const paymentModeLabel = paymentModeFilters.find((p) => p.value === paymentMode)?.label || paymentMode;
              return (
                <span
                  key={paymentMode}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    paymentMode === 'Cash' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {paymentModeLabel}
                  <button type="button" onClick={() => togglePaymentModeFilter(paymentMode)} className="hover:opacity-70">
                    <FiX size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="overflow-hidden flex-1 relative">
        {filteredAndSortedReceipts.length > 0 ? (
          <div className="overflow-y-auto absolute inset-0">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-20 shadow-sm">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 w-12">
                    SI No
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => requestSort('receiptDate')}
                  >
                    <div className="flex items-center">
                      Receipt date {getSortIcon('receiptDate')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => requestSort('patientName')}
                  >
                    <div className="flex items-center">
                      Patient Name {getSortIcon('patientName')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200">
                    Department
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200">
                    Payment Mode
                  </th>
                  {canShowPhReports && (
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => requestSort('totalAmount')}
                    >
                      <div className="flex items-center">
                        Amount {getSortIcon('totalAmount')}
                      </div>
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200">
                    Invoice Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedReceipts.map((receipt, index) => {
                  const totalAmount = (receipt.cashAmount || 0) + (receipt.bankAmount || 0);
                  return (
                    <tr
                      key={`${receipt.receiptNo}-${receipt.sequence}-${index}`}
                      className={`${
                        hoverRow === index
                          ? 'bg-gradient-to-r from-indigo-50 to-purple-50 shadow-sm'
                          : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      } transition-all duration-200 border-b border-gray-100`}
                      onMouseEnter={() => setHoverRow(index)}
                      onMouseLeave={() => setHoverRow(null)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800">
                        {index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800">
                        {formatReceiptDateTime(receipt.receiptDate)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                        {receipt.patientName || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                          Pharmacy
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                          receipt.cashAmount > 0
                            ? 'bg-green-100 text-green-800'
                            : receipt.bankAmount > 0
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                        }`}>
                          {receipt.paymentMode || 'N/A'}
                        </span>
                      </td>
                      {canShowPhReports && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-800">
                          <div className="flex items-center">
                            <MdCurrencyRupee className="text-indigo-600 mr-1" size={14} />
                            {formatAmount(totalAmount)}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm">
                        {receipt.invoiceDetails?.length > 0 ? (
                          <div className="flex flex-wrap gap-2 items-center">
                            {receipt.invoiceDetails.map((invoice, invIndex) => {
                              const invDateRaw = invoice.InvDate ?? invoice.invDate;
                              const invDateLabel = invDateRaw ? formatInvoiceDateLabel(invDateRaw) : null;
                              return (
                                <div key={invIndex} className="flex flex-wrap items-center gap-1.5">
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                                    Pharmacy
                                  </span>
                                  <span className="text-xs text-gray-600">
                                    Inv#{invoice.InvoiceNo}
                                  </span>
                                  {invDateLabel && (
                                    <span className="text-[11px] text-slate-500 whitespace-nowrap">
                                      ({invDateLabel})
                                    </span>
                                  )}
                                  {canShowPhReports && (
                                    <span className="text-xs font-medium text-gray-700">
                                      ₹{formatAmount(invoice.PayingAmout || 0)}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm flex flex-row gap-2">
                        <button
                          type="button"
                          className={`p-1.5 rounded-md transition-colors ${
                            canCancelPayment
                              ? 'bg-red-50 text-red-600 hover:bg-red-100'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                          onClick={() =>
                            handleCancelPayment(receipt?.receiptNo, receipt?.sequence, receipt?.patientName)
                          }
                          disabled={!canCancelPayment}
                          title={canCancelPayment ? 'Cancel payment' : "You don't have permission to cancel payments"}
                        >
                          <FiTrash2 size={16} />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
                          onClick={() => handlePrintReceipt(receipt)}
                          title="Print receipt"
                        >
                          <FiPrinter size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FiFileText className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500">No receipts found</p>
            </div>
          </div>
        )}
      </div>

      {generalsettings?.RecieptTemplate === 'A5' ? (
        <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px' }}>
          <ReceiptNormalComponent
            selectedDate={selectedDate}
            sysInfo={sysInfo}
            clientInfo={clientInfo}
            generalsettings={generalsettings}
            receiptData={receiptData}
            username={userInfo?.UserName || 'Admin'}
          />
        </div>
      ) : (
        <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px' }}>
          <ReceiptKotComponent
            selectedDate={selectedDate}
            sysInfo={sysInfo}
            clientInfo={clientInfo}
            generalsettings={generalsettings}
            receiptData={receiptData}
            username={userInfo?.UserName || 'Admin'}
          />
        </div>
      )}
    </div>
  );
};

function PharmacyCollectionReport() {
  const embedded = useContext(ReportEmbedContext);
  const [fromDate, setFromDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('accessToken');

  const [groupedData, setGroupedData] = useState([]);
  const [totalReceipts, setTotalReceipts] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalCash, setTotalCash] = useState(0);
  const [totalBank, setTotalBank] = useState(0);

  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);

  const [userInfo, setUserInfo] = useState({});
  const [permissions, setPermissions] = useState({});
  const [permissionsReady, setPermissionsReady] = useState(false);
  const [generalSettings, setGeneralSettings] = useState({});
  const [sysInfo, setSysInfo] = useState({});
  const [clientInfo, setClientInfo] = useState({});

  const [stats, setStats] = useState({
    stat1: { title: 'Total Receipts', value: '0' },
    stat2: { title: 'Total Amount', value: '₹ 0' },
    stat3: { title: 'Cash in Hand', value: '₹ 0' },
    stat4: { title: 'Bank Amount', value: '₹ 0' },
  });

  const isElectron = () => window && window.process && window.process.type;

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  const processPermissions = (roles) => {
    const permissionsMap = {};
    if (!Array.isArray(roles)) return permissionsMap;
    roles.forEach((role) => {
      (role?.modules || []).forEach((module) => {
        permissionsMap[module.moduleName] = module.permissions;
      });
    });
    return permissionsMap;
  };

  const hasPermission = (moduleName, action) => permissions[moduleName]?.[action] || false;

  const canShowPhReports =
    permissionsReady &&
    (userInfo?.Designation === 'Admin' || hasPermission('ShowPhReports', 'Create'));

  useEffect(() => {
    if (!permissionsReady) return;
    getCollectionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsReady]);

  useEffect(() => {
    const fetchData = async () => {
      let userRolesData;
      let userinfo;
      let generalSetting;
      let sysinfo;
      let client_info;

      if (isElectron()) {
        userRolesData = await ipcRenderer.invoke('store-get', 'user_role');
        userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
        generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
        sysinfo = await ipcRenderer.invoke('store-get', 'sysInfo');
        client_info = await ipcRenderer.invoke('store-get', 'clientInfo');
        setUserInfo(userinfo);
      } else {
        initializeWebUserInfo();
        userRolesData = getWebUserInfo().user_role;
        userinfo = getWebUserInfo().userInfo;
        generalSetting = getWebUserInfo().generalSettings || getWebUserInfo().generalOpSettings;
        sysinfo = getWebUserInfo().sysInfo;
        client_info = getWebUserInfo().clientInfo;
        setUserInfo(userinfo);
      }

      setPermissions(processPermissions(userRolesData));
      setGeneralSettings(generalSetting || {});
      setSysInfo(sysinfo || {});
      setClientInfo(client_info || {});
      setPermissionsReady(true);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to parse LineJsonData
  const parseLineJsonData = (lineJsonData) => {
    if (!lineJsonData) return [];
    try {
      return JSON.parse(lineJsonData);
    } catch (error) {
      console.error('Error parsing LineJsonData:', error);
      return [];
    }
  };

  const getCollectionData = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${BASE_URL}${GET_RECIEPT_REPORT}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'fromDate': fromDate,
          'toDate': toDate,
        },
      });

      if (response.status === 200 && response.data.isSucess) {
        const rawReceipts = response.data.data || [];
        
        // Filter only pharmacy receipts (Department = 'SI' or 'Pharmacy', or TransType = 'SI')
        let pharmacyReceipts = rawReceipts.filter(receipt => {
          // Check if Department is Pharmacy/SI
          if (receipt.Department === 'SI' || receipt.Department === 'Pharmacy') {
            return true;
          }
          // Check if any invoice detail has TransType = 'SI'
          const invoiceDetails = parseLineJsonData(receipt.LineJsonData);
          return invoiceDetails.some(inv => inv.TransType === 'SI');
        });

        const userwiseOnly =
          String(generalSettings?.UserwiseReportonly || '').trim().toLowerCase() === 'yes';
        const isAdmin = userInfo?.Designation === 'Admin';
        if (userwiseOnly && !isAdmin) {
          pharmacyReceipts = pharmacyReceipts.filter(
            (receipt) =>
              (receipt.UserName || '').trim().toLowerCase() ===
              (userInfo?.UserName || '').trim().toLowerCase()
          );
        }
        
        // Process each receipt and parse LineJsonData
        const processedReceipts = pharmacyReceipts.map(receipt => ({
          ...receipt,
          sequence: receipt.Sequence,
          receiptNo: receipt.ReceiptNo,
          patientName: receipt.PatientName,
          receiptDate: receipt.ReceiptDate,
          cashAmount: receipt.CashAmount || 0,
          bankAmount: receipt.BankAmount || 0,
          paymentMode: receipt.PaymentMode,
          invoiceDetails: parseLineJsonData(receipt.LineJsonData).filter(inv => inv.TransType === 'SI')
        }));

        // Group receipts by date
        const grouped = processedReceipts.reduce((acc, receipt) => {
          const date = new Date(receipt.ReceiptDate).toLocaleDateString('en-CA');
          
          if (!acc[date]) {
            acc[date] = {
              date,
              receipts: [],
              totalReceipts: 0,
              totalCollection: 0,
              totalCash: 0,
              totalBank: 0
            };
          }
          
          acc[date].receipts.push(receipt);
          acc[date].totalReceipts += 1;
          acc[date].totalCollection += receipt.cashAmount + receipt.bankAmount;
          acc[date].totalCash += receipt.cashAmount;
          acc[date].totalBank += receipt.bankAmount;
          
          return acc;
        }, {});

        const groupedArray = Object.values(grouped).sort((a, b) => new Date(b.date) - new Date(a.date));
        setGroupedData(groupedArray);

        // Calculate totals
        let totalReceiptsCount = 0;
        let totalAmountSum = 0;
        let totalCashInHand = 0;
        let totalBankAmount = 0;

        processedReceipts.forEach(receipt => {
          totalReceiptsCount += 1;
          totalAmountSum += receipt.cashAmount + receipt.bankAmount;
          totalCashInHand += receipt.cashAmount;
          totalBankAmount += receipt.bankAmount;
        });

        setTotalReceipts(totalReceiptsCount);
        setTotalAmount(totalAmountSum);
        setTotalCash(totalCashInHand);
        setTotalBank(totalBankAmount);

        setStats({
          stat1: { 
            title: 'Total Receipts', 
            value: totalReceiptsCount.toString()
          },
          stat2: { 
            title: 'Total Amount', 
            value: `₹${totalAmountSum.toFixed(2)}`
          },
          stat3: { 
            title: 'Cash in Hand', 
            value: `₹${totalCashInHand.toFixed(2)}`
          },
          stat4: { 
            title: 'Bank Amount', 
            value: `₹${totalBankAmount.toFixed(2)}`
          }
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (formatType) => {
    if (!canShowPhReports) return;
    setShowDownloadOptions(false);
    if (formatType === 'excel') {
      exportToExcel();
    } else if (formatType === 'pdf') {
      exportToPDF();
    }
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
  
    // Prepare data for Excel
    const collectionData = groupedData.map(day => ({
      Date: day.date,
      'Number of Receipts': Number(day.totalReceipts || 0),
      'Total Amount': Number(day.totalCollection || 0),
      'Cash Amount': Number(day.totalCash || 0),
      'Bank Amount': Number(day.totalBank || 0)
    }));
  
    const ws = XLSX.utils.json_to_sheet(collectionData);
  
    // Add summary section
    const summaryData = [
      ['Total Receipts', totalReceipts],
      ['Total Amount', totalAmount],
      ['Total Cash', totalCash],
      ['Total Bank', totalBank],
    ];
    
    XLSX.utils.sheet_add_aoa(ws, [['', '']], { origin: -1 });
    XLSX.utils.sheet_add_aoa(ws, [['Summary', '']], { origin: -1 });
    XLSX.utils.sheet_add_aoa(ws, summaryData, { origin: -1 });
  
    XLSX.utils.book_append_sheet(wb, ws, 'Pharmacy Collection Report');
    XLSX.writeFile(wb, 'Pharmacy_Collection_Report.xlsx');
  };
  
  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text('Pharmacy Collection Report', 14, 22);
    
    doc.setFontSize(10);
    doc.text(`Date Range: ${fromDate} to ${toDate}`, 14, 30);
    
    // Add summary table
    const summaryData = [
      ['Total Receipts', totalReceipts],
      ['Total Amount', `₹ ${totalAmount.toFixed(2)}`],
      ['Total Cash', `₹ ${totalCash.toFixed(2)}`],
      ['Total Bank', `₹ ${totalBank.toFixed(2)}`],
    ];
    
    doc.autoTable({
      startY: 35,
      head: [['Summary', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [99, 102, 241] }
    });
    
    // Add collection data table
    const tableData = groupedData.map(day => [
      day.date,
      day.totalReceipts,
      `₹ ${day.totalCollection.toFixed(2)}`,
      `₹ ${day.totalCash.toFixed(2)}`,
      `₹ ${day.totalBank.toFixed(2)}`
    ]);
    
    doc.autoTable({
      startY: doc.previousAutoTable.finalY + 10,
      head: [['Date', 'Receipts', 'Total Amount', 'Cash', 'Bank']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [99, 102, 241] }
    });
    
    doc.save('Pharmacy_Collection_Report.pdf');
  };

  return (
    <div className="flex h-full bg-background">
      
      {loading && <Spinner />}
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
          <div className={`flex flex-col gap-3 ${embedded ? 'lg:flex-row lg:items-center lg:justify-end' : 'lg:flex-row lg:items-center lg:justify-between'}`}>
            {!embedded && (
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                  Collection Report
                </p>
                <h1 className="text-lg font-bold text-slate-800">
                  Pharmacy Collection Report
                </h1>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
              {/* Date Inputs */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                  From Date
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                  />
                </label>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                  To Date
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    min={fromDate}
                    className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={getCollectionData}
                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
              >
                <FiBarChart2 size={16} />
                Generate
              </button>
              <button
                type="button"
                onClick={() => {
                  setFromDate(format(new Date(), 'yyyy-MM-dd'));
                  setToDate(format(new Date(), 'yyyy-MM-dd'));
                }}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
              >
                Reset
              </button>
              <div className="relative">
                <RestrictedBlur restricted={!canShowPhReports}>
                  <button
                    type="button"
                    onClick={() => canShowPhReports && setShowDownloadOptions(!showDownloadOptions)}
                    disabled={!canShowPhReports}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500 disabled:cursor-not-allowed"
                  >
                    <FiDownload size={16} />
                    Export
                  </button>
                </RestrictedBlur>

                {showDownloadOptions && canShowPhReports && (
                  <div className="absolute right-0 mt-2 w-44 bg-white border rounded-lg shadow-lg overflow-hidden z-20">
                    <button
                      type="button"
                      onClick={() => handleDownload('excel')}
                      className="flex items-center w-full text-left px-4 py-3 hover:bg-gray-50 transition"
                    >
                      <FiFileText className="mr-2 text-green-600" /> Excel (.xlsx)
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload('pdf')}
                      className="flex items-center w-full text-left px-4 py-3 hover:bg-gray-50 transition"
                    >
                      <FiFileText className="mr-2 text-red-600" /> PDF Document
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-hidden flex flex-col">
          <div className="flex flex-col gap-3 flex-1 min-h-0">
            <HighlightMetricsModal
              isOpen={isMetricsModalOpen}
              onOpen={() => setIsMetricsModalOpen(true)}
              onClose={() => setIsMetricsModalOpen(false)}
            >
              <div className="grid gap-4 md:grid-cols-4">
                <StatCard {...stats.stat1} />
                {canShowPhReports && (
                  <>
                    <StatCard {...stats.stat2} />
                    <StatCard {...stats.stat3} />
                    <StatCard {...stats.stat4} />
                  </>
                )}
              </div>
            </HighlightMetricsModal>
        
            {/* Table Section */}
            <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0">
                <div>
                  <p className="text-sm text-slate-500">
                    Showing <span className="font-semibold text-slate-700">{groupedData.length}</span> datas for this report.
                  </p>
                </div>
              </div>
              <div className="mt-2 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                <PharmacyCollectionTable
                  bills={groupedData}
                  token={token}
                  setLoading={setLoading}
                  getCollectionData={getCollectionData}
                  canShowPhReports={canShowPhReports}
                  approvalNeededPaymentReceiptCancel={
                    generalSettings?.approvalNeededPaymentReceiptCancel === true ||
                    generalSettings?.approvalNeededPaymentReceiptCancel === 'true'
                  }
                  sysInfo={sysInfo}
                  clientInfo={clientInfo}
                  userInfo={userInfo}
                  generalsettings={generalSettings}
                />
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default withAuth(PharmacyCollectionReport);

